/**
 * Transaction Rule service layer for business logic and data operations
 * Handles backend integration with proper error handling and offline support
 */
import { TransactionRule, CreateTransactionRuleData, UpdateTransactionRuleData, TransactionRuleFilters, RuleMatchingRequest, RuleMatchingResponse } from '../types/transactionRule';
import { api, ApiError } from './api';

// Local storage keys for offline support
const TRANSACTION_RULES_CACHE_KEY = 'finstack_transaction_rules_cache';
const CACHE_TIMESTAMP_KEY = 'finstack_transaction_rules_cache_timestamp';
const LAST_SYNC_TIMESTAMP_KEY = 'finstack_transaction_rules_last_sync';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Transaction Rule service with offline support and caching
 */
export class TransactionRuleService {
  /**
   * Get cached transaction rules from localStorage
   */
  private getCachedRules(): TransactionRule[] | null {
    try {
      const cached = localStorage.getItem(TRANSACTION_RULES_CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (!cached || !timestamp) {
        return null;
      }
      
      const cacheAge = Date.now() - parseInt(timestamp);
      if (cacheAge > CACHE_DURATION) {
        // Cache expired
        this.clearCache();
        return null;
      }
      
      const rules = JSON.parse(cached) as TransactionRule[];
      return rules.map(rule => ({
        ...rule,
        createdAt: new Date(rule.createdAt),
        updatedAt: new Date(rule.updatedAt),
        lastUsedAt: rule.lastUsedAt ? new Date(rule.lastUsedAt) : undefined
      }));
    } catch (error) {
      console.error('Error reading transaction rules cache:', error);
      return null;
    }
  }

  /**
   * Cache transaction rules to localStorage
   */
  private cacheRules(rules: TransactionRule[]): void {
    try {
      const serializedRules = rules.map(rule => ({
        ...rule,
        createdAt: rule.createdAt instanceof Date ? rule.createdAt.toISOString() : rule.createdAt,
        updatedAt: rule.updatedAt instanceof Date ? rule.updatedAt.toISOString() : rule.updatedAt,
        lastUsedAt: rule.lastUsedAt instanceof Date ? rule.lastUsedAt.toISOString() : rule.lastUsedAt
      }));
      
      localStorage.setItem(TRANSACTION_RULES_CACHE_KEY, JSON.stringify(serializedRules));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem(LAST_SYNC_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error caching transaction rules:', error);
    }
  }

  /**
   * Clear transaction rules cache
   */
  private clearCache(): void {
    localStorage.removeItem(TRANSACTION_RULES_CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    localStorage.removeItem(LAST_SYNC_TIMESTAMP_KEY);
  }


  /**
   * Get the last sync timestamp
   */
  getLastSyncTimestamp(): Date | null {
    try {
      const timestamp = localStorage.getItem(LAST_SYNC_TIMESTAMP_KEY);
      return timestamp ? new Date(parseInt(timestamp)) : null;
    } catch (error) {
      console.error('Error reading last sync timestamp:', error);
      return null;
    }
  }

  /**
   * Get all transaction rules with caching support
   */
  async getAll(useCache: boolean = true, filters?: TransactionRuleFilters): Promise<TransactionRule[]> {
    // Try cache first if enabled and no filters
    if (useCache && !filters) {
      const cached = this.getCachedRules();
      if (cached) {
        return cached;
      }
    }

    try {
      const rules = await api.transactionRule.getAll(filters);
      
      // Cache the results only if no filters were applied
      if (!filters) {
        this.cacheRules(rules);
      }
      
      return rules;
    } catch (error) {
      // If API fails, try to return cached data as fallback
      if (!filters) {
        const cached = this.getCachedRules();
        if (cached) {
          console.warn('API failed, using cached transaction rules');
          return cached;
        }
      }
      
      throw error;
    }
  }

  /**
   * Get active transaction rules only
   */
  async getActive(useCache: boolean = true): Promise<TransactionRule[]> {
    try {
      return await api.transactionRule.getActive();
    } catch (error) {
      // Fallback to cached data filtered by active
      const cached = this.getCachedRules();
      if (cached && useCache) {
        console.warn('API failed, using cached active transaction rules');
        return cached.filter(rule => rule.isActive);
      }
      throw error;
    }
  }

  /**
   * Get transaction rule by ID
   */
  async getById(id: string): Promise<TransactionRule | null> {
    try {
      return await api.transactionRule.getById(id);
    } catch (error: any) {
      if (error?.code === 'NOT_FOUND') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new transaction rule
   */
  async create(ruleData: CreateTransactionRuleData): Promise<TransactionRule> {
    // Validate required fields
    if (!ruleData.description?.trim()) {
      throw new ApiError('Rule description is required', 'VALIDATION_ERROR');
    }
    if (!ruleData.naturalLanguageRule?.trim()) {
      throw new ApiError('Rule condition is required', 'VALIDATION_ERROR');
    }
    if (!ruleData.glAccountNumber?.trim()) {
      throw new ApiError('GL account number is required', 'VALIDATION_ERROR');
    }

    try {
      const newRule = await api.transactionRule.create(ruleData);
      
      // Update cache optimistically
      const cached = this.getCachedRules();
      if (cached) {
        this.cacheRules([newRule, ...cached]);
      }
      
      return newRule;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'VALIDATION_ERROR') {
        throw error;
      }
      throw new ApiError('Failed to create transaction rule', 'CREATE_ERROR', error);
    }
  }

  /**
   * Update an existing transaction rule
   */
  async update(updateData: UpdateTransactionRuleData): Promise<TransactionRule> {
    if (!updateData.id) {
      throw new ApiError('Transaction rule ID is required', 'VALIDATION_ERROR');
    }

    try {
      const updatedRule = await api.transactionRule.update(updateData);
      
      // Update cache optimistically
      const cached = this.getCachedRules();
      if (cached) {
        const updatedCache = cached.map(rule => 
          rule.id === updateData.id ? updatedRule : rule
        );
        this.cacheRules(updatedCache);
      }
      
      return updatedRule;
    } catch (error) {
      throw new ApiError('Failed to update transaction rule', 'UPDATE_ERROR', error);
    }
  }

  /**
   * Delete a transaction rule with automatic retry for stale data
   */
  async delete(id: string): Promise<void> {
    if (!id) {
      throw new ApiError('Transaction rule ID is required', 'VALIDATION_ERROR');
    }

    try {
      await api.transactionRule.delete(id);
      
      // Update cache optimistically
      const cached = this.getCachedRules();
      if (cached) {
        const filteredCache = cached.filter(rule => rule.id !== id);
        this.cacheRules(filteredCache);
      }
    } catch (error: any) {
      // Check if this might be a stale data error
      if (error?.code === 'NOT_FOUND' || 
          error?.message?.includes('not found') || 
          error?.message?.includes('does not exist')) {
        console.warn(`Rule ${id} not found on backend, refreshing cache...`);
        await this.forceRefresh();
        throw new ApiError('Rule no longer exists. Data has been refreshed, please try again.', 'STALE_DATA', error);
      }
      
      throw new ApiError('Failed to delete transaction rule', 'DELETE_ERROR', error);
    }
  }

  /**
   * Bulk delete transaction rules
   */
  async deleteBulk(ids: string[]): Promise<void> {
    if (!ids.length) {
      throw new ApiError('No transaction rule IDs provided', 'VALIDATION_ERROR');
    }

    try {
      await api.transactionRule.deleteBulk(ids);
      
      // Update cache optimistically
      const cached = this.getCachedRules();
      if (cached) {
        const idSet = new Set(ids);
        const filteredCache = cached.filter(rule => !idSet.has(rule.id));
        this.cacheRules(filteredCache);
      }
    } catch (error) {
      throw new ApiError('Failed to delete transaction rules', 'BULK_DELETE_ERROR', error);
    }
  }

  /**
   * Create multiple transaction rules at once
   */
  async createBulk(rulesData: CreateTransactionRuleData[]): Promise<any> {
    if (!rulesData.length) {
      throw new ApiError('No transaction rule data provided', 'VALIDATION_ERROR');
    }

    // Validate all rules
    for (const rule of rulesData) {
      if (!rule.description?.trim() || !rule.naturalLanguageRule?.trim() || !rule.glAccountNumber?.trim()) {
        throw new ApiError('All transaction rules must have description, condition, and GL account', 'VALIDATION_ERROR');
      }
    }

    try {
      const result = await api.transactionRule.createBulk(rulesData);
      
      // Force refresh after bulk operations to ensure cache consistency
      if (result.created && result.created.length > 0) {
        console.log(`Bulk create completed: ${result.created.length} rules created. Refreshing cache...`);
        await this.forceRefresh();
      }
      
      return result;
    } catch (error) {
      throw new ApiError('Failed to create transaction rules', 'BULK_CREATE_ERROR', error);
    }
  }

  /**
   * Update rule priorities in bulk
   */
  async bulkUpdatePriorities(priorityUpdates: Array<{id: string, priority: number}>): Promise<void> {
    if (!priorityUpdates.length) {
      throw new ApiError('No priority updates provided', 'VALIDATION_ERROR');
    }

    try {
      await api.transactionRule.bulkUpdatePriorities(priorityUpdates);
      
      // Update cache optimistically
      const cached = this.getCachedRules();
      if (cached) {
        const priorityMap = new Map(priorityUpdates.map(update => [update.id, update.priority]));
        const updatedCache = cached.map(rule => {
          const newPriority = priorityMap.get(rule.id);
          return newPriority !== undefined ? { ...rule, priority: newPriority } : rule;
        });
        this.cacheRules(updatedCache);
      }
    } catch (error) {
      throw new ApiError('Failed to update rule priorities', 'BULK_UPDATE_ERROR', error);
    }
  }

  /**
   * Find matching rule for a transaction
   */
  async matchRule(request: RuleMatchingRequest): Promise<RuleMatchingResponse> {
    try {
      return await api.transactionRule.matchRule(request);
    } catch (error) {
      throw new ApiError('Failed to match transaction rule', 'MATCH_ERROR', error);
    }
  }

  /**
   * Search transaction rules by term
   */
  async search(searchTerm: string): Promise<TransactionRule[]> {
    const rules = await this.getAll();
    
    if (!searchTerm.trim()) {
      return rules;
    }
    
    const term = searchTerm.toLowerCase();
    return rules.filter(rule => 
      rule.description.toLowerCase().includes(term) ||
      rule.naturalLanguageRule.toLowerCase().includes(term) ||
      rule.glAccountNumber.toLowerCase().includes(term) ||
      rule.glAccountName.toLowerCase().includes(term)
    );
  }

  /**
   * Refresh cache by forcing a new fetch
   */
  async refresh(): Promise<TransactionRule[]> {
    this.clearCache();
    return this.getAll(false);
  }

  /**
   * Force refresh cache (alias for refresh for clarity)
   */
  async forceRefresh(): Promise<TransactionRule[]> {
    return this.refresh();
  }

  /**
   * Validate cache against backend and detect inconsistencies
   */
  async validateCache(): Promise<{ isValid: boolean; issues: string[] }> {
    const issues: string[] = [];
    
    try {
      const cached = this.getCachedRules();
      const fresh = await api.transactionRule.getAll();
      
      if (!cached) {
        return { isValid: true, issues: [] }; // No cache to validate
      }
      
      // Check count mismatch
      if (cached.length !== fresh.length) {
        issues.push(`Rule count mismatch: cache has ${cached.length}, backend has ${fresh.length}`);
      }
      
      // Check for rules in cache but not in backend (phantom rules)
      const cachedIds = new Set(cached.map(rule => rule.id));
      const freshIds = new Set(fresh.map(rule => rule.id));
      
      const phantomRules = cached.filter(rule => !freshIds.has(rule.id));
      if (phantomRules.length > 0) {
        issues.push(`${phantomRules.length} phantom rules found in cache`);
      }
      
      // Check for rules in backend but not in cache (missing rules)
      const missingRules = fresh.filter(rule => !cachedIds.has(rule.id));
      if (missingRules.length > 0) {
        issues.push(`${missingRules.length} rules missing from cache`);
      }
      
      return {
        isValid: issues.length === 0,
        issues
      };
    } catch (error) {
      console.error('Cache validation failed:', error);
      return {
        isValid: false,
        issues: ['Cache validation failed due to network error']
      };
    }
  }

  /**
   * Auto-refresh cache if validation detects issues
   */
  async validateAndRefresh(): Promise<TransactionRule[]> {
    const validation = await this.validateCache();
    
    if (!validation.isValid) {
      console.warn('Cache validation failed:', validation.issues);
      console.log('Auto-refreshing cache...');
      return this.forceRefresh();
    }
    
    return this.getAll();
  }
}

// Export singleton instance
export const transactionRuleService = new TransactionRuleService();
export default transactionRuleService;